/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { child, push, ref, update, get, onDisconnect } from "firebase/database";
import ListState from "../../../../components/ListState/ListState";
import { rtdb } from "../../../../firebase";
import { ITelehealthCallParticipants } from "../../../../utils/types";
import useAuthContext from "../../../../hooks/useAuthContext";
import { JITSI_EVENTS, TAKEOVER_DISPOSE_TIMEOUT } from "../../constants";
import { claimCall, loadJitsiScript } from "../../utils/meeting.utils";
import useToast from "../../../../hooks/useToast";

interface JitsiComponentProps {
    clientName: string;
    roomName: string;
    clientEmail: string;
    token: string;
    userId: string;
    onConferenceJoined?: () => void;
}

declare global {
    interface Window {
        JitsiMeetExternalAPI: new (domain: string, options: any) => any;
    }
}

export default function JitsiComponent({
    roomName,
    clientName,
    clientEmail,
    token,
    userId,
    onConferenceJoined,
}: JitsiComponentProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [wasTakenOver, setWasTakenOver] = useState(false);

    const { user } = useAuthContext();
    const { toast } = useToast();

    const apiRef = useRef<any>(null);
    const wasTakenOverRef = useRef(false);
    const onConferenceJoinedRef = useRef(onConferenceJoined);
    onConferenceJoinedRef.current = onConferenceJoined;
    const toastRef = useRef(toast);
    toastRef.current = toast;
    const callOptionsRef = useRef({
        displayName: clientName,
        email: clientEmail,
        jwt: token,
        participantUserId: userId,
        authUser: user,
    });
    callOptionsRef.current = {
        displayName: clientName,
        email: clientEmail,
        jwt: token,
        participantUserId: userId,
        authUser: user,
    };

    const navigate = useNavigate();
    const domain = import.meta.env.VITE_MEDCON_SERVER_URL;

    // Initialize Jitsi meeting
    useEffect(() => {
        let isCancelled = false;
        let takeoverTimeout: number | undefined;

        const handleUpdateCallParticipantsCollection = async (
            status: "active" | "inactive"
        ) => {
            const { displayName, participantUserId } = callOptionsRef.current;

            const collection = await get(
                child(ref(rtdb), `/telehealth_calls/${roomName}`)
            );

            const participants: ITelehealthCallParticipants[] =
                collection.exists() ? Object.values(collection.val()) : [];

            const participantId =
                participants.find(
                    (participant) => participant.user_id === participantUserId
                )?.id ||
                push(child(ref(rtdb), `/telehealth_calls/${roomName}`)).key;

            if (!participantId) {
                Sentry.logger.error(
                    "Could not add user details to call history object",
                    {
                        roomName,
                        clientName: displayName,
                        userId: participantUserId,
                    }
                );

                return;
            }

            const participantRef = ref(
                rtdb,
                `/telehealth_calls/${roomName}/${participantId}`
            );

            try {
                await update(participantRef, {
                    name: displayName,
                    user_id: participantUserId,
                    role: "client",
                    status,
                    id: participantId,
                    updated_at: new Date().toISOString(),
                    session_history_id: roomName,
                });

                if (status === "active") {
                    await onDisconnect(participantRef).update({
                        status: "inactive",
                    });
                } else {
                    await onDisconnect(participantRef).cancel();
                }
            } catch (err) {
                Sentry.logger.error(
                    "Could not add user details to call history object",
                    {
                        roomName,
                        clientName: displayName,
                        userId: participantUserId,
                    }
                );
            }
        };

        const handleCallTakenOver = () => {
            isCancelled = true;
            wasTakenOverRef.current = true;

            setWasTakenOver(true);

            apiRef.current?.executeCommand("hangup");

            takeoverTimeout = window.setTimeout(() => {
                if (!apiRef.current) {
                    return;
                }

                apiRef.current.dispose();
                apiRef.current = null;

                handleUpdateCallParticipantsCollection("inactive");
            }, TAKEOVER_DISPOSE_TIMEOUT);
        };

        const handlePageHide = (event: PageTransitionEvent) => {
            if (event.persisted) {
                return;
            }

            apiRef.current?.executeCommand("hangup");
        };

        const setupEventListeners = () => {
            if (!apiRef.current) return;

            const { participantUserId, authUser } = callOptionsRef.current;

            const refreshParticipantsPanel = () => {
                apiRef.current.executeCommand("refreshParticipantsPane");
            };

            // Core participant events
            const events = [
                "participantJoined",
                "participantLeft",
                "displayNameChange",
                "videoMuteStatusChanged",
                "audioMuteStatusChanged",
                "participantRoleChanged",
                "trackAdded",
                "trackRemoved",
            ];

            events.forEach((event) => {
                apiRef.current.on(event, () => {
                    refreshParticipantsPanel();
                    Sentry.logger.info("Jitsi event occurred", {
                        event,
                        userId: participantUserId,
                    });
                });
            });

            apiRef.current.on("videoConferenceJoined", () => {
                if (authUser) {
                    handleUpdateCallParticipantsCollection("active");
                }

                onConferenceJoinedRef.current?.();

                Sentry.logger.info("Jitsi event occurred", {
                    event: "videoConferenceJoined",
                    userId: participantUserId,
                });
            });

            apiRef.current.on(
                JITSI_EVENTS.CONFERENCE_FAILED,
                (data: { error: string }) => {
                    Sentry.logger.info("Jitsi event occurred", {
                        event: JITSI_EVENTS.CONFERENCE_FAILED,
                        error: data.error,
                        userId: participantUserId,
                        roomName,
                    });
                }
            );

            apiRef.current.on(
                JITSI_EVENTS.CONNECTION_FAILED,
                (data: { error: string }) => {
                    Sentry.logger.info("Jitsi event occurred", {
                        event: JITSI_EVENTS.CONNECTION_FAILED,
                        error: data.error,
                        userId: participantUserId,
                        roomName,
                    });
                }
            );

            apiRef.current.on(
                JITSI_EVENTS.MIC_ERROR,
                (data: { type: string; message: string }) => {
                    toastRef.current({
                        mode: "error",
                        message:
                            "We could not connect your microphone. Check that another tab, window or app is not already using it.",
                        duration: 10000,
                    });

                    Sentry.logger.info("Jitsi event occurred", {
                        event: JITSI_EVENTS.MIC_ERROR,
                        type: data.type,
                        message: data.message,
                        userId: participantUserId,
                        roomName,
                    });
                }
            );

            apiRef.current.on(
                JITSI_EVENTS.CAMERA_ERROR,
                (data: { type: string; message: string }) => {
                    toastRef.current({
                        mode: "error",
                        message:
                            "We could not connect your camera. Check that another tab, window or app is not already using it.",
                        duration: 10000,
                    });

                    Sentry.logger.info("Jitsi event occurred", {
                        event: JITSI_EVENTS.CAMERA_ERROR,
                        type: data.type,
                        message: data.message,
                        userId: participantUserId,
                        roomName,
                    });
                }
            );

            apiRef.current.on(
                JITSI_EVENTS.ERROR_OCCURRED,
                (data: {
                    error: { isFatal: boolean; type: string; message: string };
                }) => {
                    Sentry.logger.info("Jitsi event occurred", {
                        event: JITSI_EVENTS.ERROR_OCCURRED,
                        ...data.error,
                        userId: participantUserId,
                        roomName,
                    });
                }
            );

            // Navigation events
            apiRef.current.on("readyToClose", () => {
                handleUpdateCallParticipantsCollection("inactive");

                Sentry.logger.info("Jitsi event occurred", {
                    event: "readyToClose",
                    userId: participantUserId,
                });

                apiRef.current?.dispose();
                apiRef.current = null;

                if (wasTakenOverRef.current) {
                    return;
                }

                navigate("/");
            });
        };

        const initJitsi = () => {
            const { displayName, email, jwt, participantUserId } =
                callOptionsRef.current;

            try {
                const options = {
                    roomName,
                    width: "100%",
                    height: "92dvh",
                    parentNode: document.getElementById("jitsi-container"),
                    jwt,
                    userInfo: {
                        displayName,
                        email,
                    },
                    configOverwrite: {
                        disableDeepLinking: true,
                        enableDisplayNameInStats: true,
                        enableFeatures: "participants-pane",
                        hideConferenceSubject: true,
                        startWithAudioMuted: false,
                        startWithVideoMuted: true,
                        prejoinPageEnabled: false,
                        disableInviteFunctions: true,
                        disableSimulcast: true,
                        enableClosePage: false,
                        enableWelcomePage: false,
                        p2p: {
                            enabled: false,
                            stunServers: [
                                {
                                    urls:
                                        import.meta.env
                                            .VITE_MEDCON_STUN_SERVER_URL ||
                                        "stun:stun.l.google.com:19302",
                                },
                            ],
                        },
                        e2eping: {
                            pingInterval: -1,
                            analyticsInterval: -1,
                        },
                    },
                    interfaceConfigOverwrite: {
                        RECENT_LIST_ENABLED: true,
                        DISABLE_PRESENCE_STATUS: false,
                        DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
                        SHOW_CHROME_EXTENSION_BANNER: false,
                        MOBILE_APP_PROMO: false,
                        HIDE_INVITE_MORE_HEADER: true,
                        DISABLE_VIDEO_BACKGROUND: true,
                        filmStripOnly: false,
                        disablePolls: true,
                        disableRecording: true,
                        disableFeedback: true,
                        disableKickOut: true,
                        disableInviteFunctions: true,
                        disableChat: true,
                        disableRaiseHand: true,
                        disableVideoQualityLabel: true,
                        SHOW_BRAND_WATERMARK: false,
                        SHOW_WATERMARK_FOR_GUESTS: false,
                        SHOW_POWERED_BY: false,
                        TOOLBAR_ALWAYS_VISIBLE: true,
                        TOOLBAR_TIMEOUT: 0,
                    },
                    onload: () => {
                        setIsLoading(false);

                        if (apiRef.current) {
                            apiRef.current.executeCommand(
                                "displayName",
                                displayName
                            );
                        }
                    },
                };

                apiRef.current = new window.JitsiMeetExternalAPI(
                    domain,
                    options
                );

                // Set up event listeners
                setupEventListeners();
            } catch (err) {
                Sentry.captureException(err);
                Sentry.logger.error("Failed to initialize meeting", {
                    userId: participantUserId,
                });
                setError("Failed to initialize meeting");
                setIsLoading(false);
            }
        };

        const releaseCallClaim = claimCall(roomName, handleCallTakenOver);

        window.addEventListener("pagehide", handlePageHide);

        loadJitsiScript(domain)
            .then(() => {
                if (isCancelled) {
                    return;
                }

                initJitsi();
            })
            .catch((err) => {
                Sentry.captureException(err);
                Sentry.logger.error("Failed to load Jitsi API", {
                    userId: callOptionsRef.current.participantUserId,
                });
                setError("Failed to load Jitsi API");
                setIsLoading(false);
            });

        return () => {
            isCancelled = true;

            window.clearTimeout(takeoverTimeout);
            window.removeEventListener("pagehide", handlePageHide);
            releaseCallClaim();

            apiRef.current?.dispose();
            apiRef.current = null;
        };
    }, [domain, navigate, roomName]);

    return (
        <>
            {isLoading && !wasTakenOver ? (
                <div className="justify-center h-[92vh] flex items-center">
                    <span className="mid-loader" />
                </div>
            ) : null}
            {!isLoading && error && !wasTakenOver ? (
                <div className="h-full text-sm font-medium flex items-center justify-center">
                    <ListState
                        isError
                        errorMsg="Could not load meeting at this time"
                        stateHelperText="Try reloading this page to solve this issue"
                        context="general"
                        cardClass="h-[92svh]"
                    />
                </div>
            ) : null}
            {wasTakenOver ? (
                <div className="h-full text-sm font-medium flex items-center justify-center">
                    <ListState
                        isError={false}
                        emptyMessage="This session was opened in another tab"
                        stateHelperText="We ended the call here so your other tab can use your microphone and camera."
                        context="general"
                        cardClass="h-[92svh]"
                        emptyBtnProps={{
                            showButton: true,
                            buttonText: "Rejoin session",
                            onButtonClick: () => window.location.reload(),
                        }}
                    />
                </div>
            ) : null}
            <div
                id="jitsi-container"
                style={{
                    display: isLoading || wasTakenOver ? "none" : "block",
                    paddingBottom: "10px",
                }}
            />
        </>
    );
}
